
import { useEffect, useState } from 'react';

interface HeaderProps {
  isMenuOpen: boolean;
  setIsMenuOpen: (value: boolean) => void;
}

export default function Header({ isMenuOpen, setIsMenuOpen }: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMenuOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg' : 'bg-transparent'
      }`}
    >
      <nav className="w-full px-6 lg:px-12 py-4">
        <div className="flex items-center justify-between">
          <h1 className="flex items-center gap-3 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <img 
              src="https://public.readdy.ai/ai/img_res/3bff8f98d07cd9735bb2f12a43001bf9.jpg" 
              alt="勝手屋 - 神奈川県横須賀市の日本刀・甲冑・兜買取専門店" 
              title="勝手屋 日本刀買取 甲冑買取 兜買取"
              className="h-14 w-auto"
            />
            <div>
              <span className={`text-2xl lg:text-3xl font-black font-serif ${isScrolled ? 'text-samurai-dark' : 'text-white'}`}>
                勝手屋
              </span>
              <p className={`text-xs lg:text-sm ${isScrolled ? 'text-samurai-gray' : 'text-white/90'}`}>
                日本刀・甲冑・兜の買取専門店
              </p>
            </div>
          </h1>

          <div className="hidden lg:flex items-center gap-8">
            <a
              href="#service"
              onClick={(e) => { e.preventDefault(); scrollToSection('service'); }}
              className={`text-base font-medium transition-colors whitespace-nowrap ${
                isScrolled ? 'text-samurai-dark hover:text-samurai-red' : 'text-white hover:text-samurai-gold'
              }`}
            >
              サービス紹介
            </a>
            <a
              href="#flow"
              onClick={(e) => { e.preventDefault(); scrollToSection('flow'); }}
              className={`text-base font-medium transition-colors whitespace-nowrap ${
                isScrolled ? 'text-samurai-dark hover:text-samurai-red' : 'text-white hover:text-samurai-gold'
              }`}
            >
              査定の流れ
            </a>
            <a
              href="#guarantee"
              onClick={(e) => { e.preventDefault(); scrollToSection('guarantee'); }}
              className={`text-base font-medium transition-colors whitespace-nowrap ${
                isScrolled ? 'text-samurai-dark hover:text-samurai-red' : 'text-white hover:text-samurai-gold'
              }`}
            >
              高価買取保証
            </a>
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}
              className={`text-base font-medium transition-colors whitespace-nowrap ${
                isScrolled ? 'text-samurai-dark hover:text-samurai-red' : 'text-white hover:text-samurai-gold'
              }`}
            >
              お問い合わせ
            </a>
            <a
              href="#faq"
              onClick={(e) => { e.preventDefault(); scrollToSection('faq'); }}
              className={`text-base font-medium transition-colors whitespace-nowrap ${
                isScrolled ? 'text-samurai-dark hover:text-samurai-red' : 'text-white hover:text-samurai-gold'
              }`}
            >
              よくある質問
            </a>
            <a
              href="https://lin.ee/Bi3KvRO"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <img 
                src="https://scdn.line-apps.com/n/line_add_friends/btn/ja.png" 
                alt="LINEで日本刀・甲冑・兜買取の相談 友だち追加" 
                title="LINEで買取相談"
                height="36" 
                className="h-9"
              />
            </a>
          </div>

          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center"
            aria-label="メニュー"
          >
            <i className={`text-2xl ${isScrolled ? 'text-samurai-dark' : 'text-white'} ${isMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>

        {isMenuOpen && (
          <div className="lg:hidden mt-4 bg-white rounded-lg shadow-xl p-6 space-y-4">
            <a
              href="#service"
              onClick={(e) => { e.preventDefault(); scrollToSection('service'); }}
              className="block w-full text-left text-lg font-medium text-samurai-dark hover:text-samurai-red transition-colors py-2 whitespace-nowrap"
            >
              サービス紹介
            </a>
            <a
              href="#flow"
              onClick={(e) => { e.preventDefault(); scrollToSection('flow'); }}
              className="block w-full text-left text-lg font-medium text-samurai-dark hover:text-samurai-red transition-colors py-2 whitespace-nowrap"
            >
              査定の流れ
            </a>
            <a
              href="#guarantee"
              onClick={(e) => { e.preventDefault(); scrollToSection('guarantee'); }}
              className="block w-full text-left text-lg font-medium text-samurai-dark hover:text-samurai-red transition-colors py-2 whitespace-nowrap"
            >
              高価買取保証
            </a>
            <a
              href="#contact"
              onClick={(e) => { e.preventDefault(); scrollToSection('contact'); }}
              className="block w-full text-left text-lg font-medium text-samurai-dark hover:text-samurai-red transition-colors py-2 whitespace-nowrap"
            >
              お問い合わせ
            </a>
            <a
              href="#faq"
              onClick={(e) => { e.preventDefault(); scrollToSection('faq'); }}
              className="block w-full text-left text-lg font-medium text-samurai-dark hover:text-samurai-red transition-colors py-2 whitespace-nowrap"
            >
              よくある質問
            </a>
            <a
              href="https://lin.ee/Bi3KvRO"
              rel="noopener noreferrer"
              className="block w-full bg-[#06C755] text-white px-6 py-3 rounded-lg font-bold text-lg hover:bg-[#06C755]/90 transition-colors text-center whitespace-nowrap"
            >
              <i className="ri-line-fill mr-2"></i>
              LINEで問い合わせ
            </a>
            <a
              href="tel:070-8453-9292"
              className="block w-full bg-samurai-red text-white px-6 py-3 rounded-lg font-bold text-lg hover:bg-samurai-red/90 transition-colors text-center whitespace-nowrap"
            >
              <i className="ri-phone-fill mr-2"></i>
              今すぐ電話
            </a>
          </div>
        )}
      </nav>
    </header>
  );
}
